// The Schedule of Work: choose what goes in, then make the PDF — saved to the
// job's SharePoint site + listed in CRM, and opened from there in a new tab.
// It lives only on SharePoint, so it needs signal (docs/SCHEDULE-OF-WORK.md).
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown, Loader2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { approvedSourceOf, jobPricesOf } from '@higgins/m2q-pricing';
import { ScheduleAreas } from '../components/schedule-areas';
import { ScheduleSettings } from '../components/schedule-settings';
import { ScheduleWording } from '../components/schedule-wording';
import { getJob, listPhotos, listRecords } from '../lib/device-db';
import { useDeviceQuery, useJobRefresh, useOnline } from '../lib/hooks';
import { msalInstance } from '../lib/msal';
import { currentVersion } from '../lib/options';
import { loadAccessTags, loadSurfaceTypes } from '../lib/reference';
import { coverNote } from '../lib/schedule/cover-note';
import { openWaitingTab } from '../lib/file-handoff';
import { allPhotos, generateSchedule, measureAreas, paintsFor, saveToJob, scheduleReference } from '../lib/schedule/generate';
import { productIdsToPrice } from '../lib/use-paints';
import { scheduleAreas, totalsOf, type HoursSource, type ScheduleOptions } from '../lib/schedule/model';
import { scheduleSystems } from '../lib/schedule/systems';
import { auToday, formatDate, isoToday } from '../lib/dates';
import { Notice } from '../components/message';
import { SavedDocumentResult, type SavedResult } from '../components/saved-document-result';

const defaults = (areaIds: string[], coverPhotoId: string | null): ScheduleOptions => ({
  hoursSource: 'measured', showM2: true, showHours: true, showLitres: true, showSurfaces: true,
  areaIds, leadIns: {}, includePhotos: true, coverPhotoId, leftOutPhotoIds: [],
});

function useScheduleData(jobId: string, measureId: string) {
  return useDeviceQuery(async () => {
    const [job, records, types, tags] = await Promise.all([getJob(jobId), listRecords(jobId), loadSurfaceTypes(), loadAccessTags()]);
    const areas = measureAreas(records, measureId);
    const onDevice = (await Promise.all(areas.map((a) => listPhotos(a.id)))).flat();
    const photos = allPhotos(areas, { records, onDevice, removed: job?.removedPhotoIds });
    // The cover picked on the iPad (★), if that photo is still on one of the areas (the lookup; the older text copy).
    const measure = records.find((r) => r.id === measureId);
    const cover = measure?.fields.hig_coverimageid ?? measure?.fields.hig_coverphotoid;
    const coverPhotoId = typeof cover === 'string' && photos.some((p) => p.id === cover) ? cover : null;
    const current = currentVersion(records, measureId);
    const option = scheduleSystems(current?.version ?? null, current?.options ?? []);
    const jobPrices = jobPricesOf(records, measureId);
    // One read gives the wash rate now, the option's paint rates (litres as priced) and the hire list for Make the PDF (#530).
    const reference = await scheduleReference(jobId, current?.version ?? null, option && paintsFor(option, jobPrices));
    const systems = option && { ...option, paints: reference.paints ?? [] };
    // The cover's date is the day measuring was done ("Measuring done"), else the day the document is made.
    const coverDate = formatDate(measure?.fields.hig_measuredon) ?? auToday();
    return { job, areas, onDevice, photos, coverPhotoId, coverDate, types: types.byId, tags, reference, washRate: reference.washRate, version: current?.version ?? null, systems, jobPrices };
  }, [jobId, measureId]);
}

export default function SchedulePage() {
  const { jobId = '', measureId = '' } = useParams();
  const data = useScheduleData(jobId, measureId);
  const [options, setOptions] = useState<ScheduleOptions | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<SavedResult | null>(null);
  const online = useOnline();
  useJobRefresh(data?.job?.number);

  // First load: every area included, in the measure's order; the project's cover photo.
  useEffect(() => {
    if (data && !options) setOptions(defaults(data.areas.map((a) => a.id), data.coverPhotoId));
  }, [data, options]);

  const built = useMemo(() => (data && options ? scheduleAreas(data.areas, data.types, data.tags, data.photos, options, data.systems, undefined, data.washRate) : []), [data, options]);
  const writeNote = () =>
    coverNote({
      site: data?.job?.details?.site ?? null,
      date: data?.coverDate ?? auToday(),
      estimator: data?.job?.details?.estimator ?? null,
      areas: built.map((a) => ({ name: a.name, side: a.side, scopeNotes: a.scopeNotes, exclusions: a.exclusions, defects: a.defects })),
      totals: options?.hoursSource === 'none' ? null : totalsOf(built),
    });
  useEffect(() => {
    if (options && note === null && data) setNote(writeNote());
  }, [options, data]); // eslint-disable-line react-hooks/exhaustive-deps -- once, when the areas are first known

  if (!data || !options) return null;
  if (!data.job) return <p className="text-sm text-slate-600">This job isn&rsquo;t on this device. Open it first.</p>;
  const job = data.job;

  const available: Record<HoursSource, boolean> = {
    none: true,
    measured: true,
    // Once the REVIEW has chosen the hours for an area: then it prints exactly the hours priced.
    approved: data.areas.some((a) => approvedSourceOf(a.fields) !== null),
  };
  const change = (c: Partial<ScheduleOptions>) => setOptions({ ...options, ...c });
  const categories = [...new Set(built.flatMap((a) => a.access.map((b) => b.category)))];

  async function make() {
    if (!options || !data?.job) return;
    setResult(null);
    // It opens from SharePoint in a new tab, iPad too (lib/file-handoff.ts).
    const tab = openWaitingTab('Making the Schedule of Work…');
    try {
      // Read again only if it couldn't be read when the page opened (no signal then): for the hire list only.
      // The wash rate stays the one the screen and the cover note were worked out with, so the PDF matches them.
      let reference = data.reference;
      if (!reference.hireItems) {
        setBusy('Loading the hire list…');
        reference = await scheduleReference(jobId, data.version, data.systems && paintsFor(data.systems, data.jobPrices));
      }
      const { hireItems } = reference;
      const names = { hireItems: hireItems ?? [], jobPrices: data.jobPrices };
      // Without the hire list a hire item keeps its ticked name, so the message says so.
      const hireNote = hireItems ? '' : " The hire list couldn't be loaded, so hire items show the names ticked on site.";
      // Litres stay the ones the screen was worked out with, so the PDF matches it; say so when those aren't the priced ones.
      const paintNote = data.reference.paints === null && data.systems && productIdsToPrice(data.systems.scope).length ? " The option's price-book paints couldn't be read, so litres use the surfaces' own spread rates." : '';
      const generated = await generateSchedule(
        { job: data.job, areas: data.areas, version: data.version, options, systems: data.systems, names, washRate: data.washRate, cover: { preparedBy: msalInstance.getActiveAccount()?.name ?? null, date: data.coverDate, coverNote: note ?? '' }, types: data.types, tags: data.tags, photos: data.photos, onDevice: data.onDevice, isoDate: isoToday() },
        setBusy,
      );
      setBusy('Saving to the job…');
      const saved = await saveToJob(generated, data.version);
      if ('webUrl' in saved) {
        if (tab) tab.show(saved.webUrl);
        setResult({ ok: true, text: `${generated.fileName} — saved to the job's Proposal & Take-Off folder and ${tab ? tab.where : 'ready to open'}.${hireNote}${paintNote}`, url: saved.webUrl });
        return;
      }
      // It lives only on SharePoint: a failed save says why, and making it again is the way round it.
      tab?.close();
      setResult({ ok: false, text: `The Schedule of Work wasn't saved to the job: ${saved.problem} Make it again once that's sorted.` });
    } catch (err) {
      tab?.close();
      setResult({ ok: false, text: `Couldn't make the document: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <Link to={`/job/${jobId}`} className="flex items-center gap-1 text-sm text-slate-600">
          <ArrowLeft className="h-4 w-4" /> Areas
        </Link>
        <h1 className="text-base font-semibold text-slate-900">{job.number} · Schedule of Work</h1>
      </div>
      {!job.details && <Notice>Reopen this job with signal to bring in its client, site and estimator for the cover.</Notice>}
      <ScheduleSettings options={options} available={available} onChange={change} />
      <ScheduleWording coverNote={note ?? ''} onCoverNote={setNote} onRegenerate={() => setNote(writeNote())} categories={categories} leadIns={options.leadIns} onLeadIn={(c, t) => change({ leadIns: { ...options.leadIns, [c]: t } })} />
      <ScheduleAreas areas={data.areas} photos={data.photos} onDevice={data.onDevice} options={options} onChange={change} />
      <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white/95 py-3">
        <Button onClick={() => void make()} disabled={busy !== null || options.areaIds.length === 0 || !online}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />} Make the PDF
        </Button>
        <span className="text-sm text-slate-600">
          {busy ??
            (!online
              ? "Needs signal: the PDF is made and saved straight to the job's SharePoint folder."
              : options.areaIds.length === 0
                ? 'Include at least one area.'
                : `${options.areaIds.length} area${options.areaIds.length === 1 ? '' : 's'}`)}
        </span>
        <SavedDocumentResult result={result} className="w-full" />
      </div>
    </div>
  );
}
